import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useSearchParams } from 'react-router-dom';
import { Card, Row, Col, Statistic, Table, DatePicker, Select, message, Tag, Rate, Spin } from 'antd';
import {
  LineChartOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  FileTextOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import templateService from 'services/templateService';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const { RangePicker } = DatePicker;

/**
 * TemplateAnalytics - Comprehensive analytics dashboard for template usage
 */
const TemplateAnalytics = () => {
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get('templateId');

  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);
  const [selectedTemplate, setSelectedTemplate] = useState(templateId || 'all');

  // Analytics data
  const [aggregateStats, setAggregateStats] = useState(null);
  const [generationHistory, setGenerationHistory] = useState([]);
  const [popularTemplates, setPopularTemplates] = useState([]);
  const [chartData, setChartData] = useState({
    generationsOverTime: { labels: [], datasets: [] },
    successRateByTemplate: { labels: [], datasets: [] },
    usageByLevel: { labels: [], datasets: [] },
  });

  useEffect(() => {
    loadAnalytics();
  }, [dateRange, selectedTemplate]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const filters = {
        startDate: dateRange[0] ? dateRange[0].toISOString() : undefined,
        endDate: dateRange[1] ? dateRange[1].toISOString() : undefined,
      };

      // Load all analytics data
      const [stats, history, popular] = await Promise.all([
        templateService.getAggregateStats(filters),
        templateService.getGenerationHistory({ ...filters, page: 1, pageSize: 20 }),
        templateService.getPopularTemplates({ ...filters, limit: 10 }),
      ]);

      if (stats.success) {
        setAggregateStats(stats.data);
      }

      if (history.success) {
        setGenerationHistory(history.data);
      }

      if (popular.success) {
        setPopularTemplates(popular.data);
        prepareChartData(popular.data);
      }
    } catch (error) {
      message.error('Failed to load analytics: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const prepareChartData = (popularData) => {
    // Prepare chart data from popular templates
    const templateNames = popularData.map(t => t.templateName);
    const generationCounts = popularData.map(t => t.count);
    const successRates = popularData.map(t => t.successRate);

    setChartData({
      generationsOverTime: {
        labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4'], // Placeholder
        datasets: [{
          label: 'Generations',
          data: [65, 59, 80, 81],
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
        }],
      },
      successRateByTemplate: {
        labels: templateNames.slice(0, 5),
        datasets: [{
          label: 'Success Rate (%)',
          data: successRates.slice(0, 5),
          backgroundColor: 'rgba(54, 162, 235, 0.6)',
        }],
      },
      usageByLevel: {
        labels: ['Page', 'Module', 'Dashboard', 'Executive'],
        datasets: [{
          label: 'Usage Count',
          data: [45, 30, 15, 10], // Placeholder
          backgroundColor: [
            'rgba(255, 99, 132, 0.6)',
            'rgba(54, 162, 235, 0.6)',
            'rgba(255, 206, 86, 0.6)',
            'rgba(75, 192, 192, 0.6)',
          ],
        }],
      },
    });
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
    },
  };

  const historyColumns = [
    {
      title: 'Date',
      dataIndex: 'generatedAt',
      key: 'generatedAt',
      render: (val) => formatDate(val),
    },
    {
      title: 'Template',
      dataIndex: 'templateName',
      key: 'templateName',
    },
    {
      title: 'Organization',
      dataIndex: 'organizationName',
      key: 'organizationName',
    },
    {
      title: 'User',
      dataIndex: 'userName',
      key: 'userName',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (val) => (
        <Tag color={val === 'SUCCESS' ? 'green' : 'red'}>{val}</Tag>
      ),
    },
    {
      title: 'Duration',
      dataIndex: 'generationTimeMs',
      key: 'generationTimeMs',
      render: (val) => `${(val / 1000).toFixed(2)}s`,
    },
    {
      title: 'Tokens',
      dataIndex: 'tokensUsed',
      key: 'tokensUsed',
      render: (val) => formatNumber(val),
    },
    {
      title: 'Model',
      dataIndex: 'modelUsed',
      key: 'modelUsed',
    },
    {
      title: 'Rating',
      dataIndex: 'userRating',
      key: 'userRating',
      render: (val) => (val ? <Rate disabled value={val} /> : '-'),
    },
  ];

  const popularColumns = [
    {
      title: 'Template',
      dataIndex: 'templateName',
      key: 'templateName',
    },
    {
      title: 'Generations',
      dataIndex: 'count',
      key: 'count',
      sorter: (a, b) => a.count - b.count,
    },
    {
      title: 'Success Rate',
      dataIndex: 'successRate',
      key: 'successRate',
      render: (val) => `${val}%`,
    },
    {
      title: 'Avg Rating',
      dataIndex: 'avgRating',
      key: 'avgRating',
      render: (val) => <Rate disabled value={val} />,
    },
  ];

  if (loading && !aggregateStats) {
    return (
      <LoadingContainer>
        <Spin size="large" tip="Loading analytics..." />
      </LoadingContainer>
    );
  }

  return (
    <Container>
      <Header>
        <TitleSection>
          <PageTitle>Template Analytics</PageTitle>
          <Subtitle>Monitor template usage, performance, and user feedback</Subtitle>
        </TitleSection>

        <FiltersSection>
          <RangePicker
            onChange={(dates) => setDateRange(dates || [null, null])}
            style={{ width: 300 }}
          />
        </FiltersSection>
      </Header>

      {/* Summary Cards */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Generations"
              value={aggregateStats?.totalGenerations || 0}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Success Rate"
              value={aggregateStats?.successRate || 0}
              precision={1}
              suffix="%"
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Avg Duration"
              value={aggregateStats?.avgDuration ? (aggregateStats.avgDuration / 1000).toFixed(2) : 0}
              suffix="s"
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Tokens"
              value={formatNumber(aggregateStats?.totalTokens || 0)}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Charts */}
      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col xs={24} lg={12}>
          <Card title="Generations Over Time">
            <Line data={chartData.generationsOverTime} options={chartOptions} />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Success Rate by Template">
            <Bar data={chartData.successRateByTemplate} options={chartOptions} />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col xs={24} lg={12}>
          <Card title="Usage by Analysis Level">
            <Doughnut data={chartData.usageByLevel} options={chartOptions} />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Popular Templates" extra={<TrophyOutlined style={{ color: '#faad14' }} />}>
            <Table
              columns={popularColumns}
              dataSource={popularTemplates}
              pagination={{ pageSize: 5 }}
              size="small"
            />
          </Card>
        </Col>
      </Row>

      {/* Detailed History */}
      <Card title="Generation History" style={{ marginTop: 24 }}>
        <Table
          columns={historyColumns}
          dataSource={generationHistory}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 1200 }}
        />
      </Card>
    </Container>
  );
};

const Container = styled.div`
  padding: 24px;
  background: #f5f5f5;
  min-height: 100vh;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
`;

const TitleSection = styled.div`
  flex: 1;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  margin: 0;
  color: #262626;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #8c8c8c;
  margin: 4px 0 0 0;
`;

const FiltersSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background: #f5f5f5;
`;

export default TemplateAnalytics;
